import "@reach/combobox/styles.css";
import {
  useState,
  Suspense,
  useRef,
  Dispatch,
  SetStateAction,
  ReactNode,
  KeyboardEvent,
  MutableRefObject,
  InputHTMLAttributes,
} from "react";
import styled from "styled-components/macro";
import {
  Combobox as ReachComboBox,
  ComboboxInput,
  ComboboxList,
  ComboboxOption,
  ComboboxPopover,
} from "@reach/combobox";
import { matchSorter } from "match-sorter";
import { THEME_COLOR } from "../vars";
import { Close } from "./Icons";
import Button from "./Button";
import { useSkipioApi } from "../api";
import useDebounce from "../hooks/use-debounce";
import { Label, inputCss } from "./Input";
import usePages, { PaginatingEndpoint } from "../hooks/use-pages";
import useIsReadonly from "../hooks/use-is-readonly";
import { UrlBuilder } from "@synvox/api";

type Transformers = {
  idToItem: (apis: Record<"skipioApi", UrlBuilder<any>>, key: string) => any;
  itemToString: (item: any) => string;
};

function Combobox({
  label,
  addLabel,
  value = [],
  onChange,
  allowString = false,
  // This method may suspend (used to load existing tiles)
  idToItem = (_, x) => x,
  // This method cannot suspend (used in existing tiles and search)
  itemToString = (x) => String(x),
  endpoint = () => () => ({ meta: { totalCount: 0 }, data: [] }),
  params = {},
  readOnly = false,
  children,
  onAddUnknown,
  disabled,
  ...props
}: {
  label?: string;
  addLabel?: string;
  value: string[];
  onChange: Dispatch<SetStateAction<string[]>>;
  allowString?: boolean;
  endpoint?: (apis: { skipioApi: UrlBuilder<any> }) => PaginatingEndpoint<any>;
  params?: Record<string, any>;
  readOnly?: boolean;
  children?: ReactNode;
  onAddUnknown?: (val: string) => void;
  disabled?: boolean;
} & Partial<Transformers> &
  Omit<InputHTMLAttributes<HTMLInputElement>, "value" | "onChange">) {
  const inputRef = useRef<HTMLInputElement | null>(null);
  const skipioApi = useSkipioApi();
  const [inputValue, setInputValue] = useState("");
  const keyMap = useRef<Record<string, string>>({});
  const debouncedQuery = useDebounce(inputValue);
  let { data: items = [] } = usePages(
    endpoint({ skipioApi }),
    {
      ...params,
      query: debouncedQuery.trim(),
    },
    1
  );

  disabled = useIsReadonly() || disabled;

  if (!debouncedQuery.trim()) items = [];

  function onSubmit(val = inputValue) {
    const item = items.find((item) =>
      itemToString(item).toLowerCase().startsWith(val.toLowerCase())
    ) ||
      items[0] || { id: "" };

    const newId = allowString ? val.trim() : item.id;
    if (!newId) {
      if (val && onAddUnknown) {
        onAddUnknown(val);
        setInputValue("");
      }

      return;
    }

    keyMap.current[newId] = itemToString(item);
    onChange((ids) => [...new Set([...ids, newId])]);
    setInputValue("");
  }

  const options = matchSorter(items.map(itemToString), inputValue);

  return (
    <>
      <ReachComboBox onSelect={onSubmit} openOnFocus>
        <form
          onSubmit={(e) => {
            e.preventDefault();
            onSubmit();
          }}
        >
          <FlexBar>
            <div>
              {Boolean(label) && <Label>{label}</Label>}
              <Wrapper onClick={() => inputRef.current!.focus()}>
                {Boolean(value.length) && (
                  <Tiles>
                    {value.map((id) => (
                      <IdTile data-ignore="true" key={id}>
                        <Suspense fallback={<>&nbsp;</>}>
                          <TileRender
                            idToItem={idToItem}
                            itemToString={itemToString}
                            keyMap={keyMap}
                            id={id}
                          />
                        </Suspense>{" "}
                        {!readOnly && (
                          <button
                            type="button"
                            onClick={() =>
                              onChange((ids) =>
                                ids.filter((item) => item !== id)
                              )
                            }
                          >
                            <Close />
                          </button>
                        )}
                      </IdTile>
                    ))}
                  </Tiles>
                )}
                <UnstyledInput
                  {...props}
                  disabled={disabled}
                  ref={inputRef}
                  autoComplete="off"
                  value={inputValue}
                  onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
                    setInputValue((e.target as HTMLInputElement).value);
                  }}
                  readOnly={readOnly}
                  onKeyDown={(e: KeyboardEvent) => {
                    if (inputValue !== "") return;
                    if (e.key === "Backspace") {
                      onChange((ids) => ids.slice(0, -1));
                    }
                  }}
                />
              </Wrapper>
            </div>
            {Boolean(addLabel && !readOnly) && (
              <Button
                variant="link"
                onClick={() => onSubmit()}
                style={{ transform: "translateY(-6px)" }}
                disabled={disabled}
              >
                {addLabel}
              </Button>
            )}
            {children}
          </FlexBar>
        </form>
        {options.length > 0 && (
          <ComboboxPopover>
            <ComboboxList>
              {options.map((option) => (
                <ComboboxOption key={option} value={option} />
              ))}
            </ComboboxList>
          </ComboboxPopover>
        )}
      </ReachComboBox>
    </>
  );
}

function TileRender({
  idToItem,
  itemToString,
  id,
  keyMap,
}: Transformers & {
  id: string;
  keyMap: MutableRefObject<Record<string, string>>;
}) {
  const skipioApi = useSkipioApi();
  try {
    const label = itemToString(idToItem({ skipioApi }, id));
    if (keyMap.current[id]) delete keyMap.current[id];
    return <>{label}</>;
  } catch (p) {
    if (keyMap.current[id]) return <>{keyMap.current[id]}</>;
    else throw p;
  }
}

const FlexBar = styled.div`
  display: flex;
  flex-direction: row;
  align-items: flex-end;
  & > :first-child {
    flex: 1;
  }
`;

const Wrapper = styled.div`
  ${inputCss}
  min-height: 46px;
`;

const UnstyledInput = styled(ComboboxInput)`
  border: 0;
  background: transparent;
  font-size: 14px;
  min-width: 200px;
`;

const IdTile = styled.div`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  background: ${THEME_COLOR};
  border-radius: 100px;
  padding: 4px 8px;
  line-height: 1.2em;
  margin: 2px;
  color: white;
  font-size: 12px;
  button {
    display: inline-flex;
    margin: 0;
    padding: 0;
    margin: 0;
    padding: 0;
    border: none;
    background: rgba(0, 0, 0, 0.1);
    color: white;
    align-items: center;
    justify-content: center;
    transition-duration: 0.2s;
    border-radius: 100px;
    margin-left: 4px;
    height: 16px;
    width: 16px;
    margin-right: -4px;
    &:hover {
      background: rgba(0, 0, 0, 0.2);
    }
    &:active {
      transition-duration: 0;
      background: rgba(0, 0, 0, 0.5);
    }
    svg {
      width: 12px;
      height: 12px;
    }
  }
`;

const Tiles = styled.div`
  display: inline;
  &:after {
    content: "";
    display: inline-block;
    height: 1em;
    width: 0.5em;
  }
`;

export default Combobox;
